import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../../app/hooks'
import { uploadMultimediaAttachment } from '../../../../api/multimedia/multimediaApi'
import { showMessage } from '../../../../redux/reducers/message/messageFeature'
import { AiUploadedDocument } from './AiUploadDocumentSection'
import { UploadedAttachmentState } from './aiDataExtractionRequest'
import { asAttachmentUploadState } from './aiUploadedAttachmentsPure'
import { persistMultimediaAttachmentToStore } from './persistMultimediaAttachmentToStore'
import {
  clearDocumentSelectionAndRevokePreview,
  selectFirstEligibleDocumentAndStartUpload,
} from './singleDocumentSelection'

export type DocumentAttachmentUploadSyncOptions = {
  isFloorPlan?: boolean
  selectionDocumentId: string
  getCurrentDocumentId: () => string | undefined
}

type UseAiDocumentAttachmentParams = {
  oversizeErrorMessage: string
  pickEligibleFile: (files: FileList) => File | undefined
  registerInFlightUpload: (uploadPromise: Promise<void>) => void
  setDocumentsMaxFileSizeErrorText: (errorText: string | undefined) => void
  unsupportedFileTypeErrorMessage: string
  isFloorPlanApiUpload?: boolean
}

export const useAiDocumentAttachment = ({
  oversizeErrorMessage,
  pickEligibleFile,
  registerInFlightUpload,
  setDocumentsMaxFileSizeErrorText,
  unsupportedFileTypeErrorMessage,
  isFloorPlanApiUpload,
}: UseAiDocumentAttachmentParams) => {
  const dispatch = useAppDispatch()
  const [document, setDocument] = useState<AiUploadedDocument | null>(null)
  const [errorText, setErrorText] = useState<string | undefined>()
  const [uploadedAttachment, setUploadedAttachment] = useState<UploadedAttachmentState | null>(null)
  const documentRef = useRef<AiUploadedDocument | null>(null)

  useEffect(() => {
    documentRef.current = document
  }, [document])

  const uploadDocumentAndSyncAttachmentState = useCallback(
    async (
      file: File,
      setAttachmentState: (attachmentState: UploadedAttachmentState) => void,
      options: DocumentAttachmentUploadSyncOptions
    ) => {
      const { isFloorPlan, selectionDocumentId, getCurrentDocumentId } = options
      const isCurrentSelection = () => getCurrentDocumentId() === selectionDocumentId

      try {
        const attachment = await uploadMultimediaAttachment(file, isFloorPlan)
        if (!isCurrentSelection()) {
          return
        }
        const normalizedAttachment = persistMultimediaAttachmentToStore(dispatch, attachment, file.name)
        setAttachmentState(asAttachmentUploadState('uploaded', normalizedAttachment))
      } catch {
        if (!isCurrentSelection()) {
          return
        }
        dispatch(showMessage({ message: 'unknown' }))
        setAttachmentState(asAttachmentUploadState('failed'))
      }
    },
    [dispatch]
  )

  const onSelect = useCallback(
    (fileListFromInput: FileList | null) => {
      selectFirstEligibleDocumentAndStartUpload({
        currentDocument: documentRef.current,
        fileListFromInput,
        maxFileSizeErrorMessage: oversizeErrorMessage,
        onDocumentChosen: (nextDocument) => {
          documentRef.current = nextDocument
        },
        pickEligibleFile,
        setDocument,
        setDocumentsMaxFileSizeErrorText,
        setErrorText,
        setUploadedAttachment,
        startUpload: (file, selectionDocumentId) => {
          registerInFlightUpload(
            uploadDocumentAndSyncAttachmentState(file, setUploadedAttachment, {
              isFloorPlan: isFloorPlanApiUpload,
              selectionDocumentId,
              getCurrentDocumentId: () => documentRef.current?.id,
            })
          )
        },
        unsupportedFileTypeErrorMessage,
      })
    },
    [
      oversizeErrorMessage,
      pickEligibleFile,
      registerInFlightUpload,
      setDocumentsMaxFileSizeErrorText,
      unsupportedFileTypeErrorMessage,
      uploadDocumentAndSyncAttachmentState,
      isFloorPlanApiUpload,
    ]
  )

  const onRemove = useCallback(() => {
    clearDocumentSelectionAndRevokePreview(
      documentRef.current,
      setDocument,
      setUploadedAttachment,
      () => {
        documentRef.current = null
      },
      () => {
        setErrorText(undefined)
        setDocumentsMaxFileSizeErrorText(undefined)
      }
    )
  }, [setDocumentsMaxFileSizeErrorText])

  return {
    document,
    errorText,
    uploadedAttachment,
    onSelect,
    onRemove,
  }
}
